// Étape 10 — Réglages contractuels (société, médiateur, signature, numérotation, délais, expéditeur)
import { useState, useEffect } from "react";
import axios from "axios";
import { C, Btn, Input, FormField, Alert } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` } });

const Card = ({ title, subtitle, children }) => (
  <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
    <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 4 }}>{title}</div>
    {subtitle && <div style={{ fontSize: 12, color: C.mid, marginBottom: 14 }}>{subtitle}</div>}
    {children}
  </div>
);

const Fields = ({ obj, setObj, fields, prefix, cols = 3 }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
    {fields.map(([k, label, type]) => (
      <FormField key={k} label={label}>
        <Input type={type || "text"} value={obj?.[k] ?? ""} data-testid={`${prefix}-${k}`}
          onChange={(e) => setObj({ ...obj, [k]: type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })} />
      </FormField>
    ))}
  </div>
);

const EmailStatus = () => {
  const [st, setSt] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/email/status`, hdr()).then(r => setSt(r.data)).catch(() => setSt({ error: true })); }, []);
  if (!st) return null;
  const ok = st.ready;
  return (
    <div data-testid="email-status" style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: ok ? C.green : C.amber }}>
      {st.error ? "Statut de l'envoi indisponible pour le moment" : st.provider === "brevo"
        ? (ok ? `✓ Brevo actif — envoi depuis ${st.from}, domaine ${st.domain} authentifié (SPF/DKIM) · ${st.envoyes_aujourdhui} e-mail(s) envoyé(s) aujourd'hui`
              : `⚠ Brevo : domaine ${st.domain || "auprea.com"} non authentifié — vérifiez les DNS dans Brevo`)
        : "Service e-mail géré actif"}
    </div>
  );
};

const SOCIETE = [["nom", "Dénomination"], ["forme", "Forme"], ["capital", "Capital social"], ["adresse", "Adresse du siège"], ["siret", "SIRET"],
  ["rcs", "RCS"], ["tva_intra", "N° TVA intracommunautaire"], ["president", "Président"], ["email", "E-mail"], ["telephone", "Téléphone"]];

const Signature = () => {
  const [src, setSrc] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => axios.get(`${API}/admin/reglages/signature`, { ...hdr(), responseType: "blob" })
    .then(r => setSrc(URL.createObjectURL(r.data))).catch(() => setSrc(null));
  useEffect(() => { load(); }, []);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await axios.post(`${API}/admin/reglages/signature`, fd, hdr()); setMsg("✓ Signature enregistrée"); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Erreur de téléversement"); }
  };
  return (
    <Card title="Signature du président" subtitle="PNG sur fond transparent, pré-apposée « pour AUPRÉA SAS » sur les lettres de mission et certificats.">
      <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
        <div style={{ width: 220, height: 90, border: `1px dashed ${C.border}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "#fafafa" }}>
          {src ? <img src={src} alt="Signature" style={{ maxWidth: 200, maxHeight: 80 }} data-testid="signature-preview" /> : <span style={{ fontSize: 11, color: C.muted }}>Aucune signature</span>}
        </div>
        <label style={{ cursor: "pointer" }}>
          <input type="file" accept="image/png" style={{ display: "none" }} onChange={(e) => upload(e.target.files[0])} data-testid="signature-upload" />
          <span style={{ background: C.gold, color: "#fff", padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600 }}>Téléverser un PNG</span>
        </label>
        {msg && <span style={{ fontSize: 12, color: msg.startsWith("✓") ? C.green : C.red }} data-testid="signature-msg">{msg}</span>}
      </div>
    </Card>
  );
};

export const OngletContrats = ({ r, save }) => {
  const [s, setS] = useState({
    societe: r.societe, mediateur: r.mediateur, numerotation: r.numerotation,
    relances_proposition: r.relances_proposition, delais: r.delais, expediteur: r.expediteur,
    validite_proposition_jours: r.validite_proposition_jours, circuit_propositions: r.circuit_propositions || "tous",
  });
  const [ok, setOk] = useState(false);
  const up = (k) => (v) => setS({ ...s, [k]: v });
  const rp = s.relances_proposition || {};
  const doSave = async () => {
    await save({ ...s, validite_proposition_jours: Number(s.validite_proposition_jours) });
    setOk(true); setTimeout(() => setOk(false), 2500);
  };
  const missing = ["capital", "rcs", "tva_intra"].filter(k => !s.societe?.[k]);
  return (
    <>
      {(missing.length > 0 || !s.mediateur?.nom) && (
        <Alert type="warn"><span>⚠</span><span>À compléter avant l'envoi de documents : {[...missing.map(k => SOCIETE.find(x => x[0] === k)[1]), !s.mediateur?.nom && "médiateur de la consommation"].filter(Boolean).join(", ")}.</span></Alert>
      )}
      <Card title="Nouveau circuit propositions" subtitle="Envoi des propositions signables (signature électronique, paiement, mission).">
        <select value={s.circuit_propositions} onChange={(e) => setS({ ...s, circuit_propositions: e.target.value })} data-testid="reglage-circuit"
          style={{ padding: 9, borderRadius: 8, border: "1px solid #ddd", fontFamily: "inherit" }}>
          <option value="desactive">Désactivé</option><option value="admin">Admin uniquement</option><option value="tous">Actif pour tous</option>
        </select>
      </Card>
      <Card title="Société" subtitle="Mentions légales reprises sur la proposition, la lettre de mission, les CGV et les factures.">
        <Fields obj={s.societe} setObj={up("societe")} fields={SOCIETE} prefix="societe" />
      </Card>
      <Card title="Médiateur de la consommation" subtitle="Adhésion obligatoire pour vendre aux particuliers (article L612-1 du Code de la consommation).">
        <Fields obj={s.mediateur} setObj={up("mediateur")} fields={[["nom", "Nom"], ["adresse", "Adresse"], ["site", "Site web"]]} prefix="mediateur" />
      </Card>
      <Signature />
      <Card title="Numérotation" subtitle="Continue pour tout le réseau, jamais réutilisée. Factures : F-AAAA-00001 · Avoirs : AV-AAAA-00001.">
        <Fields obj={s.numerotation} setObj={up("numerotation")} prefix="num" fields={[["prochain_proposition", "Prochain n° de proposition", "number"],
          ["prochain_facture", "Prochain n° de facture (F-AAAA-…)", "number"], ["prochain_avoir", "Prochain n° d'avoir (AV-AAAA-…)", "number"]]} />
      </Card>
      <Card title="Validité, relances et délais légaux">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          <FormField label="Validité d'une proposition (jours)">
            <Input type="number" value={s.validite_proposition_jours} onChange={(e) => setS({ ...s, validite_proposition_jours: e.target.value })} data-testid="validite-jours" />
          </FormField>
          <FormField label="Relances après envoi (jours, séparés par des virgules)">
            <Input value={(rp.jours || []).join(", ")} data-testid="relances-jours"
              onChange={(e) => up("relances_proposition")({ ...rp, jours: e.target.value.split(",").map(x => parseInt(x, 10)).filter(n => !isNaN(n)) })} />
          </FormField>
          <FormField label="Relance avant expiration (J−)">
            <Input type="number" value={rp.avant_expiration ?? ""} onChange={(e) => up("relances_proposition")({ ...rp, avant_expiration: Number(e.target.value) })} data-testid="relance-avant-expiration" />
          </FormField>
        </div>
        <div style={{ marginTop: 12 }}>
          <Fields obj={s.delais} setObj={up("delais")} prefix="delais" fields={[["paiement_hors_etablissement", "Délai de paiement hors établissement (jours)", "number"],
            ["retractation", "Délai de rétractation (jours)", "number"], ["seuil_effectif", "Seuil d'effectif petite entreprise (salariés)", "number"]]} />
        </div>
      </Card>
      <Card title="Expéditeur des e-mails">
        <Fields obj={s.expediteur} setObj={up("expediteur")} prefix="expediteur" cols={2} fields={[["email", "Adresse d'expédition"], ["nom", "Nom affiché"]]} />
        <EmailStatus />
      </Card>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Btn variant="gold" onClick={doSave} data-testid="contrats-save">Enregistrer</Btn>
        {ok && <span style={{ fontSize: 12, color: C.green }} data-testid="contrats-saved">✓ Sauvegardé</span>}
      </div>
    </>
  );
};
